import React, {useContext, useRef, useEffect, useMemo, useState} from "react";
import PropTypes from 'prop-types'
import FilterContext from "./../Context/FilterContext";
import {SearchIcon} from "@heroicons/react/outline";
import Map, {FullscreenControl, NavigationControl, ScaleControl} from "react-map-gl";
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import useSupercluster from "use-supercluster";
import {useHits} from "react-instantsearch-hooks-web";
import Cluster from "../Elements/Cluster";

// Reverse geocode -> Retrieve features from coordinates
async function reverseGeocode({ lng, lat, token }) {
    const result = await fetch('https://api.mapbox.com/geocoding/v5/mapbox.places/' + lng + ',' + lat + '.json?access_token=' + token)

    return result.json()
}

const MainMap = props => {
    const { token } = props
    const {
        zoom,
        setZoom,
        lat,
        lng,
        setLat,
        setLng,
        showRestartSearch,
        setShowRestartSearch,
        triggerNewPositionSearch,
        updateFromGeocoder,
        setUpdateFromGeocoder,
        setAddressText,
        setLoading
    } = useContext(FilterContext)

    const { hits } = useHits();
    const map = useRef()

    async function onClick() {
        const result = await reverseGeocode({lng, lat, token})

        if (result.features.length > 0) {
            setAddressText(result.features[0].place_name)
        }

        triggerNewPositionSearch()
        setShowRestartSearch(false)
    }

    const onDragEnd = value => {
        setLng(value.viewState.longitude.toFixed(5))
        setLat(value.viewState.latitude.toFixed(5))
        setShowRestartSearch(true)
    }

    const onZoomEnd = value => {
        setLng(value.viewState.longitude.toFixed(5))
        setLat(value.viewState.latitude.toFixed(5))
        setZoom(value.viewState.zoom.toFixed(2))
        setViewport({ ...value.viewState });
    }

    useEffect(() => {
        if (updateFromGeocoder) {
            setLoading(true)
            map.current.flyTo({
                center: [lng, lat]
            })
        }
    }, [updateFromGeocoder])

    const onMoveEnd = () => {
        // get map bounds for bounding box search
        // console.log(map.current.getBounds())

        if (updateFromGeocoder) {
            triggerNewPositionSearch()
            setUpdateFromGeocoder(false)
        }
    }

    const [viewport, setViewport] = useState({
        longitude: lng,
        latitude: lat,
        zoom: zoom
    })

    const points = useMemo(() =>
        hits.filter(hit => hit._geoloc).map(hit => ({
            type: "Feature",
            id: 'a-' + hit.id,
            properties: {
                cluster: false,
                ...hit
            },
            geometry: {
                type: "Point",
                coordinates: [
                    parseFloat(hit._geoloc.lng),
                    parseFloat(hit._geoloc.lat)
                ]
            }
        })), [hits]
    )

    const bounds = map.current
        ? map.current
            .getMap()
            .getBounds()
            .toArray()
            .flat()
        : null

    const { clusters, supercluster } = useSupercluster({
        points,
        bounds,
        zoom: viewport.zoom,
        options: {
            radius: 100,
            maxZoom: 22
        }
    });

    useEffect(() => {
        if (viewport.fromCluster) {
            map.current.flyTo({
                center: [viewport.longitude, viewport.latitude],
                zoom: viewport.zoom
            })
        }
    }, [viewport])

    return (
        <div className={'w-full h-full'}>
            <div className={'absolute z-10 inset-0 pointer-events-none flex items-end justify-center p-2'}>
                {
                    showRestartSearch ?
                        <button onClick={onClick} className={'px-4 py-2 rounded-lg shadow bg-white text-sm text-gray-600 hover:bg-indigo-800 hover:text-white pointer-events-auto flex items-center gap-2'}>
                            <SearchIcon className={'w-4 h-4'} />
                            <span>Relancer la recherche ici</span>
                        </button> :
                        null
                }
            </div>
            <Map
                ref={map}
                width="100%"
                height="100%"
                mapStyle="mapbox://styles/mapbox/streets-v11"
                mapboxAccessToken={token}
                initialViewState={viewport}
                onDragEnd={onDragEnd}
                onZoomEnd={onZoomEnd}
                onMoveEnd={onMoveEnd}
            >

                {/*<DrawControl />*/}
                {/*<GeocoderControl {...props}/>*/}
                <FullscreenControl />
                <ScaleControl position='bottom-right'/>
                <NavigationControl position='bottom-left'/>
                {
                    clusters.map(cluster => (
                        <Cluster
                            key={'cluster-' + cluster.id}
                            cluster={cluster}
                            supercluster={supercluster}
                            props={{ setViewport, setZoom, zoom }}
                        />
                    ))
                }
            </Map>
        </div>
    )
}

MainMap.propTypes = {
    zoom: PropTypes.string,
    addressLng: PropTypes.string,
    addressLat: PropTypes.string
}

export default MainMap